import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { RuntimeConfigService } from '../services/common/runtime-config.service';

// Why this exists: after a deploy, customers/QA kept seeing stale responses
// for hours because intermediary caches (browser disk cache, corporate
// proxies) re-served old bodies. Asking for a fresh copy on every API call
// is cheap and removes an entire class of "why doesn't my change show up"
// incidents. Hashed JS/CSS bundles are unaffected — they are content-
// addressable and never take this code path.
//
// Scoped to API calls only: app-local static assets (e.g. /assets/*.json
// taxonomy files, icons) are intentionally left to their normal browser
// caching. `/assets/config.json` already opts itself out via
// RuntimeConfigService's `cache: 'no-store'` fetch call.
//
// When RuntimeConfig.cacheDebug is true, every API / hub response is
// logged with its cache-relevant headers so ops can spot a proxy or
// browser still serving stale bodies.
export const noCacheInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const url = new URL(req.url, location.origin);
  const isApiCall =
    url.pathname.startsWith('/api/') || url.pathname.startsWith('/hubs/');

  if (!isApiCall) {
    return next(req);
  }

  const withNoCache = req.clone({
    headers: req.headers
      .set('Cache-Control', 'no-cache, no-store, must-revalidate')
      .set('Pragma', 'no-cache')
      .set('Expires', '0'),
  });

  const cacheDebug = inject(RuntimeConfigService).cacheDebug;
  if (!cacheDebug()) {
    return next(withNoCache);
  }

  return next(withNoCache).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        // eslint-disable-next-line no-console
        console.debug('[cache-debug] api response', {
          url: req.urlWithParams,
          method: req.method,
          status: event.status,
          headers: {
            'cache-control': event.headers.get('Cache-Control'),
            age: event.headers.get('Age'),
            etag: event.headers.get('ETag'),
            'last-modified': event.headers.get('Last-Modified'),
            date: event.headers.get('Date'),
            'x-cache': event.headers.get('X-Cache'),
          },
        });
      }
    }),
  );
};
